import { z } from "zod";
import { IdSchema, IsoDateTimeSchema } from "./primitives.js";
/**
 * Encounter diagnoses contracts (20260906151703): the coded diagnoses a
 * clinician saves on one visit through `api.save_encounter_diagnoses`, and the
 * rows the signed-note history, the encounter workspace and the writer receipt
 * return. Replace-set while the encounter is open; frozen once it is signed.
 * Shared by the Agent API route, the Supabase adapter and clinical-web.
 */
export const ENCOUNTER_DIAGNOSES_MAX = 12;
export const EncounterDiagnosisCodeSystemSchema = z.enum(["ICD-10-CA", "ICD-10-CM", "SNOMED-CT"]);
export const EncounterDiagnosisSourceSchema = z.enum(["clinician_selected", "synthesis_suggested"]);
/** Mirrors the per-item allow-list of api.save_encounter_diagnoses exactly. */
export const EncounterDiagnosisWriteSchema = z
    .object({
    codeSystem: EncounterDiagnosisCodeSystemSchema,
    code: z.string().trim().min(1).max(80),
    display: z.string().trim().min(1).max(240),
    /** Defaults to clinician_selected on the server. */
    source: EncounterDiagnosisSourceSchema.optional(),
})
    .strict();
/** One saved row: rank is the 1-based position the clinician listed it at. */
export const EncounterDiagnosisSchema = z
    .object({
    id: IdSchema,
    codeSystem: EncounterDiagnosisCodeSystemSchema,
    code: z.string().min(1),
    display: z.string().min(1),
    rank: z.coerce.number().int().positive(),
    source: EncounterDiagnosisSourceSchema,
    savedAt: IsoDateTimeSchema,
    savedBy: IdSchema.nullable().optional(),
})
    .passthrough();
function duplicateCodeIndex(diagnoses) {
    const seen = new Set();
    for (const [index, diagnosis] of diagnoses.entries()) {
        const key = `${diagnosis.codeSystem}:${diagnosis.code.toUpperCase()}`;
        if (seen.has(key))
            return index;
        seen.add(key);
    }
    return -1;
}
/**
 * PUT /v1/encounters/:encounterId/diagnoses body. The set replaces what the
 * encounter holds; a duplicate code within it is refused before the request
 * leaves the client (the database refuses it with 22023 as well).
 */
export const SaveEncounterDiagnosesRequestSchema = z
    .object({
    siteId: IdSchema,
    expectedEncounterVersion: z.number().int().nonnegative(),
    diagnoses: z.array(EncounterDiagnosisWriteSchema).max(ENCOUNTER_DIAGNOSES_MAX),
})
    .strict()
    .superRefine((request, context) => {
    const duplicate = duplicateCodeIndex(request.diagnoses);
    if (duplicate >= 0) {
        context.addIssue({
            code: "custom",
            path: ["diagnoses", duplicate, "code"],
            message: "Each diagnosis code may appear once per encounter",
        });
    }
});
export const SaveEncounterDiagnosesReceiptSchema = z
    .object({
    encounterId: IdSchema,
    encounterVersion: z.coerce.number().int().positive(),
    diagnoses: z.array(EncounterDiagnosisSchema),
})
    .passthrough();
//# sourceMappingURL=encounter-diagnoses.js.map